import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import type { ProjectTask, SkillItem } from '../types';

// Completed work: done tasks + explored skills, scoped to the intern (+ shared).
export function useDone() {
  const { user } = useAuth();
  const [tasks, setTasks] = useState<ProjectTask[]>([]);
  const [skills, setSkills] = useState<SkillItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchDone = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    setError(null);
    try {
      const scope = `assignee_id.is.null,assignee_id.eq.${user.id}`;

      const { data: doneTasks, error: tErr } = await supabase
        .from('intern_project_tasks_view')
        .select('*')
        .or(scope)
        .eq('status', 'done')
        .order('completed_at', { ascending: false })
        .limit(100);
      if (tErr) throw tErr;

      const { data: exploredSkills, error: sErr } = await supabase
        .from('intern_skill_items')
        .select('*')
        .or(scope)
        .eq('status', 'explored')
        .order('updated_at', { ascending: false })
        .limit(100);
      if (sErr) throw sErr;

      setTasks((doneTasks as ProjectTask[]) ?? []);
      setSkills((exploredSkills as SkillItem[]) ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load completed work.');
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    fetchDone();
  }, [fetchDone]);

  const reopenTask = useCallback(async (id: string) => {
    const { error } = await supabase.rpc('set_intern_task_status', {
      p_task_id: id,
      p_status: 'in_progress',
    });
    if (!error) setTasks((prev) => prev.filter((t) => t.id !== id));
    return { error };
  }, []);

  const reopenSkill = useCallback(async (id: string) => {
    const { error } = await supabase.rpc('set_intern_skill_progress', {
      p_skill_id: id,
      p_status: 'exploring',
      p_notes: null,
    });
    if (!error) setSkills((prev) => prev.filter((s) => s.id !== id));
    return { error };
  }, []);

  return { tasks, skills, loading, error, refresh: fetchDone, reopenTask, reopenSkill };
}
